/**
 * Marketing-partner authentication API.
 *
 * POSTs to /api/v2/signin/phone on lbtsoapi-dev.nemo-ig.tech (proxied as
 * /user-api in dev). The username is a phone number; skipCaptcha is sent so
 * we don't need a captcha token in this flow.
 *
 * Test credentials: phone `100000000001`, password `1111`.
 *
 * Response fields we care about (note capitalized casing):
 *   Role, UserId, DisplayName, Username, TerminalName, token
 */

import partnerAxios from 'utils/partnerAxios';

export interface PartnerLoginPayload {
    username: string;
    password: string;
    mpId?: string;
    enclosureId?: string | number;
}

export interface PartnerUser {
    userId?: string | number;
    username: string;
    displayName: string;
    role?: string;
    terminalName?: string;
    enclosureId?: string | number;
    operatorId?: string | number;
    mpId?: string;
}

export interface PartnerLoginResponse {
    token: string;
    user: PartnerUser;
}

/**
 * Walk an arbitrary response object looking for the first property that
 * matches any of the candidate names (case-insensitive). Returns undefined
 * if nothing is found. Lets us pick fields without knowing whether the API
 * puts them at the root, under `body`, under `userInfo`, etc.
 */
const findKey = (obj: any, candidates: string[]): any => {
    if (obj === null || typeof obj !== 'object') return undefined;
    const wanted = candidates.map((c) => c.toLowerCase());
    const queue: any[] = [obj];
    while (queue.length) {
        const node = queue.shift();
        if (node === null || typeof node !== 'object') continue;
        for (const key of Object.keys(node)) {
            if (wanted.includes(key.toLowerCase()) && node[key] != null) {
                return node[key];
            }
        }
        for (const key of Object.keys(node)) {
            const v = node[key];
            if (v && typeof v === 'object') queue.push(v);
        }
    }
    return undefined;
};

export const partnerLogin = async (payload: PartnerLoginPayload): Promise<PartnerLoginResponse> => {
    let response;
    try {
        response = await partnerAxios.post('/fixedsigninlbtso/cardid', {
            username: payload.username,
            password: payload.password,
            skipCaptcha: true,
            ...(payload.enclosureId != null ? { enclosureId: payload.enclosureId } : {})
        });
    } catch (err: any) {
        // Surface the API's own message (e.g. "Invalid enclosure ID") instead
        // of axios's generic "Request failed with status code 400".
        const apiMessage =
            err?.response?.data?.error?.message ??
            err?.response?.data?.message ??
            err?.message ??
            'Login failed';
        throw new Error(apiMessage);
    }

    const data: any = response.data ?? {};
    if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
    }

    const token = findKey(data, ['token', 'accessToken', 'access_token']);
    if (!token) {
        throw new Error('Login succeeded but no token was returned');
    }

    const username = findKey(data, ['Username', 'username']) ?? payload.username;
    const userId = findKey(data, ['UserId', 'userId']);
    const displayName = findKey(data, ['DisplayName', 'displayName']) ?? username;
    const role = findKey(data, ['Role', 'role']);
    const terminalName = findKey(data, ['TerminalName', 'terminalName', 'terminal_name', 'TerminalId']);
    const enclosureId = findKey(data, ['EnclosureId', 'Enclosureid', 'enclosureId', 'enclosure_id']);
    const operatorId = findKey(data, ['OperatorId', 'operatorId', 'operator_id']);

    return {
        token,
        user: {
            userId,
            username,
            displayName,
            role,
            terminalName,
            enclosureId,
            operatorId,
            mpId: payload.mpId
        }
    };
};
